import errorHandler from "common/errorHandler";
import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import * as actions from "./actions";

function* getSnowflakeConnections(api) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/snowflake`);
    return result.data;
}

function* createSnowflakeConnection(api, connection) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/snowflake`, connection);
    return result.data;
}

function* getSnowflakeConnection(api, connectionId) {
    try {
        let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}`);
        return result.data;
    }
    catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateSnowflakeConnection(api, connectionId, updatedConnection) {
    let response = yield call([api, api.put], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}`, updatedConnection);
    return response.data;
}

function* deleteSnowflakeConnection(api, connectionId) {
    yield call([api, api.delete], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}`);
}

function* getSnowflakeConnectionTables(api, connectionId) {
    let result = yield call([api, api.get], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}/tables`);
    return result.data.data;
}

function* getSnowflakeTablePreview(api, connectionId, tableName, limit) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}/preview`, { tableName, limit });
    return result.data.data;
}

function* getSnowflakeTableSchema(api, connectionId, tableName) {
    let result = yield call([api, api.post], `${Endpoints.systemConnections}/api/v1/snowflake/item/${connectionId}/schema`, { tableName });
    return result.data.data;
}

export function* getSnowflakeConnectionsRequest(api) {
    try {
        const result = yield call(getSnowflakeConnections, api);
        yield put(actions.getSnowflakeConnectionsSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "connections");
        yield put(actions.getSnowflakeConnectionsFailure(errorObject));
    }
}

export function* createSnowflakeConnectionRequest(api, { connection }) {
    try {
        let createdConnection = yield call(createSnowflakeConnection, api, connection);
        yield put(actions.createSnowflakeConnectionSuccess(createdConnection));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "connection");
        yield put(actions.createSnowflakeConnectionFailure(errorObject));
    }
}

export function* getSnowflakeConnectionRequest(api, { connectionId }) {
    try {
        const connection = yield call(getSnowflakeConnection, api, connectionId);
        yield put(actions.getSnowflakeConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "system connection");
        yield put(actions.getSnowflakeConnectionFailure(errorObject));
    }
}

export function* updateSnowflakeConnectionRequest(api, { connectionId, updatedConnection }) {
    try {
        const connection = yield call(updateSnowflakeConnection, api, connectionId, updatedConnection);
        yield put(actions.updateSnowflakeConnectionSuccess(connection));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "connection");
        yield put(actions.updateSnowflakeConnectionFailure(errorObject));
    }
}

export function* deleteSnowflakeConnectionRequest(api, { connectionId }) {
    try {
        yield call(deleteSnowflakeConnection, api, connectionId);
        yield put(actions.deleteSnowflakeConnectionSuccess(connectionId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "connection");
        yield put(actions.deleteSnowflakeConnectionFailure(errorObject));
    }
}

export function* getSnowflakeConnectionTablesRequest(api, { connectionId }) {
    try {
        let tables = yield call(getSnowflakeConnectionTables, api, connectionId);
        yield put(actions.getSnowflakeConnectionTablesSuccess(tables));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "Snowflake connection tables");
        // Ensure the error message is properly surfaced for missing database/schema
        if (error.response && error.response.data && error.response.data.message) {
            errorObject.message = error.response.data.message;
        }
        yield put(actions.getSnowflakeConnectionTablesFailure(errorObject));
    }
}

export function* getSnowflakeTablePreviewRequest(api, { params: { connectionId, tableName, limit } }) {
    try {
        const preview = yield call(getSnowflakeTablePreview, api, connectionId, tableName, limit);
        yield put(actions.getSnowflakeTablePreviewSuccess(preview));
    } catch (error) {
        const errorObject = errorHandler(error, "Preview", "Snowflake table");
        // Ensure the error message is properly surfaced
        if (error.response && error.response.data && error.response.data.message) {
            errorObject.message = error.response.data.message;
        }
        yield put(actions.getSnowflakeTablePreviewFailure(errorObject));
    }
}

export function* getSnowflakeTableSchemaRequest(api, { params: { connectionId, tableName } }) {
    try {
        const schema = yield call(getSnowflakeTableSchema, api, connectionId, tableName);
        yield put(actions.getSnowflakeTableSchemaSuccess(schema));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "Snowflake table schema");
        // Ensure the error message is properly surfaced
        if (error.response && error.response.data && error.response.data.message) {
            errorObject.message = error.response.data.message;
        }
        yield put(actions.getSnowflakeTableSchemaFailure(errorObject));
    }
}

export function* watchCreateSnowflakeConnectionRequest(api, { params }) {
    yield call(createSnowflakeConnectionRequest, api, params);
}

export function* watchGetSnowflakeConnectionRequest(api, { params }) {
    yield call(getSnowflakeConnectionRequest, api, params);
}

export function* watchUpdateSnowflakeConnectionRequest(api, { params }) {
    yield call(updateSnowflakeConnectionRequest, api, params);
}

export function* watchDeleteSnowflakeConnectionRequest(api, { params }) {
    yield call(deleteSnowflakeConnectionRequest, api, params);
}

export function* watchGetSnowflakeConnectionTablesRequest(api, { params }) {
    yield call(getSnowflakeConnectionTablesRequest, api, params);
}

export function* watchGetSnowflakeTablePreviewRequest(api, { params }) {
    yield call(getSnowflakeTablePreviewRequest, api, { params });
}

export function* watchGetSnowflakeTableSchemaRequest(api, { params }) {
    yield call(getSnowflakeTableSchemaRequest, api, { params });
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_SNOWFLAKE_CONNECTIONS_REQUEST, getSnowflakeConnectionsRequest, api);
    yield takeEvery(actions.CREATE_SNOWFLAKE_CONNECTION_REQUEST, watchCreateSnowflakeConnectionRequest, api);
    yield takeLatest(actions.GET_SNOWFLAKE_CONNECTION_REQUEST, watchGetSnowflakeConnectionRequest, api);
    yield takeLatest(actions.UPDATE_SNOWFLAKE_CONNECTION_REQUEST, watchUpdateSnowflakeConnectionRequest, api);
    yield takeLatest(actions.DELETE_SNOWFLAKE_CONNECTION_REQUEST, watchDeleteSnowflakeConnectionRequest, api);
    yield takeLatest(actions.GET_SNOWFLAKE_CONNECTION_TABLES_REQUEST, watchGetSnowflakeConnectionTablesRequest, api);
    yield takeLatest(actions.GET_SNOWFLAKE_TABLE_PREVIEW_REQUEST, watchGetSnowflakeTablePreviewRequest, api);
    yield takeLatest(actions.GET_SNOWFLAKE_TABLE_SCHEMA_REQUEST, watchGetSnowflakeTableSchemaRequest, api);
}